import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

/** True when keystrokes are going into a text field / editable element and must not trigger shortcuts. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  const type = (target as HTMLInputElement).type;
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
}

const SEQUENCE_TIMEOUT_MS = 1000;

type Bindings = Record<string, () => void>;

/**
 * The bindings of every mounted `useHotkeys`. Each instance listens on its own, so each must know the others'
 * sequences: after `g`, a page's single `d` must not fire when the app binds `g d` — whichever listener runs first.
 */
const mounted = new Set<RefObject<Bindings>>();

const isBound = (combo: string) => [...mounted].some((bindings) => combo in bindings.current);
const startsSequence = (key: string) =>
  [...mounted].some((bindings) => Object.keys(bindings.current).some((combo) => combo.startsWith(`${key} `)));

/**
 * Global single-key and two-key sequence shortcuts, e.g. `{ 'g d': goDashboard, '?': showHelp }`.
 * The second key of a sequence must follow within 1 s; a key that completes any mounted sequence is never also
 * a single-key shortcut. Ignored while typing, with Ctrl/Meta/Alt held, or when another handler already called
 * `preventDefault()`.
 */
export function useHotkeys(bindings: Bindings): void {
  const latest = useRef(bindings);
  useLayoutEffect(() => {
    latest.current = bindings;
  });

  useEffect(() => {
    mounted.add(latest);
    let previous: { key: string; at: number } | null = null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.repeat) return;
      if (isTypingTarget(event.target)) return;
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      const now = event.timeStamp || performance.now();

      // Every instance tracks every key (handled or not) so they all agree on which sequence is in progress.
      const first = previous && now - previous.at <= SEQUENCE_TIMEOUT_MS ? previous.key : null;
      previous = { key, at: now };
      if (first !== null && isBound(`${first} ${key}`)) {
        previous = null;
        const handler = latest.current[`${first} ${key}`];
        if (handler && !event.defaultPrevented) {
          event.preventDefault();
          handler();
        }
        return;
      }
      if (startsSequence(key) || event.defaultPrevented) return;
      const single = latest.current[key];
      if (single) {
        event.preventDefault();
        single();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      mounted.delete(latest);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);
}

/** App-wide navigation shortcuts: g d / g l / g t / g c / g o. */
export const NAVIGATION_HOTKEYS: Record<string, { path: string; label: string }> = {
  'g d': { path: '/', label: 'Go to Dashboard' },
  'g l': { path: '/leads', label: 'Go to Leads' },
  'g t': { path: '/tasks', label: 'Go to Tasks' },
  'g c': { path: '/calendar', label: 'Go to Calendar' },
  'g o': { path: '/opportunities', label: 'Go to Opportunities' },
};

export function useNavigationHotkeys(): void {
  const navigate = useNavigate();
  const bindings: Record<string, () => void> = {};
  for (const [combo, { path }] of Object.entries(NAVIGATION_HOTKEYS)) bindings[combo] = () => void navigate(path);
  useHotkeys(bindings);
}
